import React, { createContext, useContext } from "react";
import type { ReactNode } from "react";
import type { TokenData } from "../types/chat.types";

interface AuthContextType {
  getToken?: () => Promise<TokenData>;
  getEntraToken?: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  getToken?: () => Promise<TokenData>;
  getEntraToken?: () => Promise<string | null>;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, getToken, getEntraToken }) => {
  return (
    <AuthContext.Provider value={{ getToken, getEntraToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const useAccessToken = (): (() => Promise<string>) => {
  const { getToken } = useAuth();
  
  return async (): Promise<string> => {
    if (!getToken || typeof getToken !== "function") {
      console.warn("No getToken function available");
      return "";
    }
    
    try {
      const tokenData = await getToken();
      return tokenData.publicAccessToken;
    } catch (error) {
      console.error("Failed to retrieve token:", error);
      return "";
    }
  };
};

export const useEntraToken = (): (() => Promise<string | null>) => {
  const { getEntraToken } = useAuth();
  
  return async (): Promise<string | null> => {
    if (!getEntraToken || typeof getEntraToken !== "function") {
      // Entra token is optional, so no warning needed
      return null;
    }
    
    try {
      return await getEntraToken();
    } catch (error) {
      console.error("Failed to retrieve Entra token:", error);
      return null;
    }
  };
};

export const useTokenData = (): (() => Promise<TokenData | null>) => {
  const { getToken } = useAuth();

  return async (): Promise<TokenData | null> => {
    if (!getToken || typeof getToken !== "function") {
      console.warn("No getToken function available");
      return null;
    }

    try {
      const tokenData = await getToken();
      return tokenData;
    } catch (error) {
      console.error("Failed to retrieve token:", error);
      return null;
    }
  };
};
